import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  getCatalogDecorationUrl,
  MAP_NOTE_PIN_BANNERS,
  MAP_NOTE_PIN_COLOR_LABEL,
  MAP_NOTE_PIN_COLORS,
  MAP_NOTE_PIN_HEX_COLORS,
  MAP_NOTE_PIN_OVERLAY_ID,
  type MapNotePinColor,
} from "@west-marches/shared";

import { cn } from "@/lib/classNames";

interface MapNotePinColorMenuProps {
  value: MapNotePinColor;
  onChange: (next: MapNotePinColor) => void;
  /** Optional hidden flag. When provided, the menu adds a "Hidden" swatch. */
  hidden?: boolean;
  /** Called when the user toggles between "hidden" and "color visible" states. */
  onChangeHidden?: (next: boolean) => void;
  disabled?: boolean;
  /**
   * Visual rendering scale. The trigger is the marker sprite itself; size
   * controls the displayed pixel dimensions of that trigger.
   */
  size?: "sm" | "md";
  className?: string;
}

const SIZE_PX: Record<NonNullable<MapNotePinColorMenuProps["size"]>, number> = {
  sm: 24,
  md: 32,
};

const DROPDOWN_GAP_PX = 4;
const DROPDOWN_VIEWPORT_PADDING_PX = 8;
const HIDDEN_OPACITY = 0.25;
const NO_MAP_MARKER_LABEL = "No map marker";

interface DropdownPosition {
  top: number;
  left: number;
}

function NoMapMarkerPreview({
  url,
  size,
  fallbackHex,
}: {
  url: string | null;
  size: number;
  fallbackHex: string;
}) {
  const labelFontSize = Math.max(6, Math.floor(size * 0.32));

  return (
    <span
      className="relative inline-flex items-center justify-center overflow-hidden"
      style={{ width: size, height: size }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          width={size}
          height={size}
          style={{
            position: "absolute",
            inset: 0,
            width: size,
            height: size,
            objectFit: "contain",
            opacity: HIDDEN_OPACITY,
          }}
        />
      ) : (
        <span
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundColor: fallbackHex,
            opacity: HIDDEN_OPACITY,
          }}
        />
      )}
      <span
        className="relative z-10 rounded-[4px] bg-[#fffaf1]/92 px-px font-black uppercase leading-none text-neutral-950/80"
        style={{ fontSize: labelFontSize, letterSpacing: "-0.04em" }}
      >
        NONE
      </span>
    </span>
  );
}

const MARKER_GROUPS: Array<{ label: string; markers: readonly MapNotePinColor[] }> = [
  { label: "Pins", markers: MAP_NOTE_PIN_COLORS },
  { label: "Banners", markers: MAP_NOTE_PIN_BANNERS },
];

export function MapNotePinColorMenu({
  value,
  onChange,
  hidden = false,
  onChangeHidden,
  disabled = false,
  size = "sm",
  className,
}: MapNotePinColorMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerSize = SIZE_PX[size];

  useLayoutEffect(() => {
    if (!open) return;

    function recompute() {
      const triggerEl = triggerRef.current;
      const dropdownEl = dropdownRef.current;
      if (!triggerEl) return;
      const triggerRect = triggerEl.getBoundingClientRect();
      const dropdownWidth = dropdownEl?.offsetWidth ?? 0;
      const desiredLeft = triggerRect.right - dropdownWidth;
      const maxLeft = window.innerWidth - dropdownWidth - DROPDOWN_VIEWPORT_PADDING_PX;
      const clampedLeft = Math.max(
        DROPDOWN_VIEWPORT_PADDING_PX,
        Math.min(desiredLeft, maxLeft),
      );
      setPosition({
        top: triggerRect.bottom + DROPDOWN_GAP_PX,
        left: clampedLeft,
      });
    }

    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function isInsideMenu(target: EventTarget | null): boolean {
      if (!(target instanceof Node)) return false;
      if (triggerRef.current?.contains(target)) return true;
      if (dropdownRef.current?.contains(target)) return true;
      return false;
    }

    function handleDocPointerDown(event: PointerEvent) {
      if (isInsideMenu(event.target)) return;
      setOpen(false);
      const swallowClick = (clickEvent: Event) => {
        clickEvent.stopPropagation();
        if (typeof (clickEvent as Event & {
          stopImmediatePropagation?: () => void;
        }).stopImmediatePropagation === "function") {
          (clickEvent as Event & { stopImmediatePropagation: () => void })
            .stopImmediatePropagation();
        }
        document.removeEventListener("click", swallowClick, true);
      };
      document.addEventListener("click", swallowClick, true);
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handleDocPointerDown, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handleDocPointerDown, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const currentUrl = getCatalogDecorationUrl(MAP_NOTE_PIN_OVERLAY_ID[value]);
  const supportsHidden = typeof onChangeHidden === "function";

  const triggerLabel = hidden
    ? `Marker: no map marker (${MAP_NOTE_PIN_COLOR_LABEL[value]}). Click to change.`
    : `Marker: ${MAP_NOTE_PIN_COLOR_LABEL[value]}. Click to change.`;
  const triggerTitle = hidden
    ? `Marker: no map marker`
    : `Marker: ${MAP_NOTE_PIN_COLOR_LABEL[value]}`;

  const renderMarkerOption = (marker: MapNotePinColor) => {
    const url = getCatalogDecorationUrl(MAP_NOTE_PIN_OVERLAY_ID[marker]);
    const selected = !hidden && marker === value;
    const fallbackHex = MAP_NOTE_PIN_HEX_COLORS[marker];
    return (
      <button
        key={marker}
        type="button"
        role="option"
        aria-selected={selected}
        title={MAP_NOTE_PIN_COLOR_LABEL[marker]}
        onClick={(event) => {
          event.stopPropagation();
          if (hidden && onChangeHidden) {
            onChangeHidden(false);
          }
          onChange(marker);
          setOpen(false);
        }}
        className={cn(
          "inline-flex items-center justify-center rounded border-2 p-1 transition-colors",
          selected
            ? "border-neutral-950 bg-[#fff2d9]"
            : "border-transparent hover:border-neutral-950/30 hover:bg-[#fff2d9]/70",
        )}
      >
        {url ? (
          <img
            src={url}
            alt={MAP_NOTE_PIN_COLOR_LABEL[marker]}
            width={triggerSize}
            height={triggerSize}
            style={{ width: triggerSize, height: triggerSize, objectFit: "contain" }}
          />
        ) : (
          <span
            className="block rounded-full"
            style={{
              width: triggerSize,
              height: triggerSize,
              backgroundColor: fallbackHex ?? "#cccccc",
            }}
          />
        )}
      </button>
    );
  };

  const dropdown =
    open && (typeof document !== "undefined") ? (
      createPortal(
        <div
          ref={dropdownRef}
          role="dialog"
          aria-label="Marker"
          data-map-note-popover-stop-click="true"
          style={{
            position: "fixed",
            top: position?.top ?? -9999,
            left: position?.left ?? -9999,
            visibility: position ? "visible" : "hidden",
          }}
          className={cn(
            "z-200 flex flex-col gap-2 rounded-md border-2 border-neutral-950 bg-[#fffaf1] p-2",
            "shadow-[4px_4px_0_rgba(19,19,19,0.16)]",
          )}
        >
          {MARKER_GROUPS.map((group) => (
            <div key={group.label} className="flex flex-col gap-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-600">
                {group.label}
              </div>
              <div role="listbox" aria-label={group.label} className="flex flex-row flex-wrap gap-1">
                {group.markers.map(renderMarkerOption)}
                {/*
                  Append the no-marker swatch into the colored-pin row so existing
                  callers / tests that look for it in a single
                  listbox keep working.
                */}
                {group.label === "Pins" && supportsHidden ? (
                  <button
                    key="__hidden__"
                    type="button"
                    role="option"
                    aria-selected={hidden}
                    aria-label={NO_MAP_MARKER_LABEL}
                    title={NO_MAP_MARKER_LABEL}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChangeHidden?.(true);
                      setOpen(false);
                    }}
                    className={cn(
                      "inline-flex items-center justify-center rounded border-2 p-1 transition-colors",
                      hidden
                        ? "border-neutral-950 bg-[#fff2d9]"
                        : "border-transparent hover:border-neutral-950/30 hover:bg-[#fff2d9]/70",
                    )}
                  >
                    <NoMapMarkerPreview
                      url={currentUrl}
                      size={triggerSize}
                      fallbackHex={MAP_NOTE_PIN_HEX_COLORS[value] ?? "#cccccc"}
                    />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )
    ) : null;

  return (
    <div
      data-map-note-popover-stop-click="true"
      className={cn("relative inline-flex items-center", className)}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel}
        title={triggerTitle}
        className={cn(
          "inline-flex items-center justify-center rounded-md border border-neutral-950/15 bg-[#fffaf1] p-1 transition-colors hover:border-neutral-950/40 disabled:cursor-not-allowed disabled:opacity-60",
        )}
        style={{ width: triggerSize + 8, height: triggerSize + 8 }}
      >
        {hidden ? (
          <NoMapMarkerPreview
            url={currentUrl}
            size={triggerSize}
            fallbackHex={MAP_NOTE_PIN_HEX_COLORS[value] ?? "#cccccc"}
          />
        ) : currentUrl ? (
          <img
            src={currentUrl}
            alt=""
            width={triggerSize}
            height={triggerSize}
            style={{
              width: triggerSize,
              height: triggerSize,
              objectFit: "contain",
            }}
          />
        ) : (
          <span
            className="block rounded-full"
            style={{
              width: triggerSize,
              height: triggerSize,
              backgroundColor: MAP_NOTE_PIN_HEX_COLORS[value] ?? "#cccccc",
            }}
          />
        )}
      </button>

      {dropdown}
    </div>
  );
}
